/*
PROV: COHINT037.BF.UISWITCH01
REQ: AUD-UX-10, UX-BASE-05, SYS-ARCH-15
WHY: Accessible toggle switch. Pure CSS (hidden checkbox) to avoid Radix styling conflicts.
     Visuals match shadcn/ui Switch dimensions exactly.
*/

import React from "react";
import { cn } from "./lib/utils";

type SwitchProps = {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  name?: string;
  value?: string;
  required?: boolean;
  className?: string;
  label?: string;
  id?: string;
};

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  function Switch({ className, label, checked, defaultChecked, onCheckedChange, disabled, id, ...props }, ref) {
    return (
      <label className={cn("inline-flex items-center gap-2", disabled ? "cursor-not-allowed" : "cursor-pointer")}>
        <span className={cn("relative inline-flex h-[1.15rem] w-8 shrink-0", className)}>
          <input
            {...props}
            ref={ref}
            id={id}
            type="checkbox"
            role="switch"
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={onCheckedChange ? (e) => onCheckedChange(e.target.checked) : undefined}
            className="peer sr-only"
          />
          <span
            className={cn(
              "absolute inset-0 rounded-full border border-transparent shadow-sm transition-colors",
              "bg-brand-400 peer-checked:bg-green-600",
              "dark:bg-brand-600 dark:peer-checked:bg-green-500",
              "peer-focus-visible:border-brand-300 peer-focus-visible:ring-[3px] peer-focus-visible:ring-brand-300/50",
              "dark:peer-focus-visible:border-brand-600 dark:peer-focus-visible:ring-brand-600/50",
              "peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
            )}
          />
          <span
            className={cn(
              "pointer-events-none absolute top-[1px] left-[1px] size-4 rounded-full bg-white ring-0 transition-transform",
              "peer-checked:translate-x-[calc(100%-2px)]",
              "dark:bg-brand-900 dark:peer-checked:bg-brand-900",
            )}
          />
        </span>
        {label && <span className="text-sm text-brand-700 dark:text-brand-300">{label}</span>}
      </label>
    );
  }
);

export { Switch };
export type { SwitchProps };
